import { Checkbox, Panel, PanelType } from 'office-ui-fabric-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField } from 'office-ui-fabric-react';
import { AppendixUpload } from '../../AppendixUpload';
import { PanelButtons } from './editCommentPanel/PanelButtons';
import {
  getAttr,
  getEntityId,
} from '../../../../../features/helpers/apiHelpers';
import { ConditionsPanel } from './editCommentPanel/ConditionsPanel';
import { useDispatch, useSelector } from 'react-redux';
import { getGlobalContentTypeByTypeSelector } from '../../../../../features/apiFeatures/globalContentTypeSlice';
import {
  COMMENTTYPE_HEARING_REVIEW,
  GLOBALCONTENTTYPE_TERMSANDCONDITIONS,
} from '../../../../../constants/api';
import { getLatestGlobalContentOfTypeInitialize } from '../../../../../features/actions/api';
import { getLatestGlobalContentByGlobalContentTypeId } from '../../../../../features/apiFeatures/globalContentSlice';
import { downloadFileFromUrl } from '../../../../../utilities/files';
import { mapFileListItemToFile } from '../../../../../utilities/mapperFunctions';

const EditCommentPanel = ({
  isOpen,
  onDismiss,
  commentText,
  commentType,
  onChangeText,
  onChangeFiles,
  commentFiles,
  onCreateOrUpdateComment,
  showSpinner,
  isEditMode,
  disableOnBehalfOf,
  onBehalfOf,
  onChangeOnBehalfOf,
  isOnBehalfOf,
  onChangeIsOnBehalfOf,
  commentModel,
  isHearingCreatedOrDraft,
  isHearingAwaitingConclusion,
  isCurrentUserOwnerOfHearing,
}) => {
  const { t: translator } = useTranslation();
  const dispatch = useDispatch();

  const globalContentType = useSelector(
    getGlobalContentTypeByTypeSelector(GLOBALCONTENTTYPE_TERMSANDCONDITIONS),
  );
  const globalContentTypeId = getEntityId(globalContentType);
  const globalContent = useSelector(
    getLatestGlobalContentByGlobalContentTypeId(globalContentTypeId),
  );

  const [hasInputChanged, setHasInputChanged] = useState(false);
  const [isConditionsApproved, setIsConditionsApproved] = useState(false);
  const [isConditionsPanelOpen, setIsConditionsPanelOpen] = useState(false);
  const [isHearingAnswered, setIsHearingAnswered] = useState(false);
  const [
    isTermsAndConditionsDisabled,
    setIsTermsAndConditionsDisabled,
  ] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const maxFileSizeInBytes = 10 * Math.pow(10, 6);
  const onBehalfOfMaxLength = 75;

  const onDeleteFile = (value) => {
    setHasInputChanged(true);
    value && onChangeFiles(mapFileListItemToFile(value), true);
  };

  const onBehalfOfModel = useRef(undefined);

  useEffect(() => {
    setIsTermsAndConditionsDisabled(
      commentType === COMMENTTYPE_HEARING_REVIEW ||
        (isOnBehalfOf &&
          isHearingAwaitingConclusion &&
          isCurrentUserOwnerOfHearing),
    );
  }, [
    commentType,
    isOnBehalfOf,
    isHearingAwaitingConclusion,
    isCurrentUserOwnerOfHearing,
  ]);

  useEffect(() => {
    if (globalContentTypeId) {
      dispatch(getLatestGlobalContentOfTypeInitialize(globalContentTypeId));
    }
  }, [globalContentTypeId, dispatch]);

  useEffect(() => {
    onBehalfOfModel.current = getAttr(commentModel.comment, 'onBehalfOf');
  }, [commentModel.comment]);

  useEffect(() => {
    const newIsOnBehalfOf =
      (!disableOnBehalfOf && isCurrentUserOwnerOfHearing) ||
      !!onBehalfOfModel.current;
    onChangeIsOnBehalfOf(newIsOnBehalfOf);
    onChangeOnBehalfOf(onBehalfOfModel.current || null);
  }, [
    isCurrentUserOwnerOfHearing,
    onChangeOnBehalfOf,
    disableOnBehalfOf,
    onChangeIsOnBehalfOf,
    isOpen,
  ]);

  useEffect(() => {
    if (!isOpen) {
      setIsConditionsApproved(false);
      setIsHearingAnswered(false);
      setIsButtonDisabled(true);
      setHasInputChanged(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (commentText) {
      validateHearingAnswer(commentText);
    }
  }, [commentText]);

  const calculateIsButtonDisabled = useCallback(() => {
    /* Checks if:
     * - onBehalfOf is set and entered
     * - Hearing is answered
     * */
    const onBehalfEnteredAndHearingAnswered =
      !(isOnBehalfOf && !onBehalfOf) && isHearingAnswered;
    /* Checks if:
     * - All fields are valid
     * */
    const allFieldsAreValid =
      onBehalfEnteredAndHearingAnswered && isConditionsApproved;
    /* Checks special case where a hearing is awaiting conclusion
     * but hearing owner is allowed to input an extra hearing answer
     * with required fileds: onBehalf checked, entered and a hearing answer
     * */
    const userIsOwnerAndAwaitingConclusion =
      onBehalfEnteredAndHearingAnswered &&
      isHearingAwaitingConclusion &&
      isCurrentUserOwnerOfHearing;
    const shouldButtonBeDisabled =
      !hasInputChanged ||
      (!allFieldsAreValid &&
        !isHearingCreatedOrDraft &&
        !userIsOwnerAndAwaitingConclusion);
    setIsButtonDisabled(shouldButtonBeDisabled);
  }, [
    isOnBehalfOf,
    onBehalfOf,
    isHearingAnswered,
    isConditionsApproved,
    isHearingAwaitingConclusion,
    isCurrentUserOwnerOfHearing,
    hasInputChanged,
    isHearingCreatedOrDraft,
  ]);

  useEffect(() => {
    calculateIsButtonDisabled();
  }, [calculateIsButtonDisabled]);

  const onChangeOnBehalfOfLocal = (value) => {
    setHasInputChanged(true);
    value = value && value.substring(0, onBehalfOfMaxLength);
    onChangeOnBehalfOf(value);
  };

  const validateHearingAnswer = (answer) => {
    const isValid = answer.trim().length > 0;
    setIsHearingAnswered(isValid);
  };

  const onChangeLocalAnswer = (value) => {
    setHasInputChanged(true);
    onChangeText(value);
  };

  const OnChangeFilesLocal = useCallback(
    (value) => {
      setHasInputChanged(true);
      onChangeFiles(value);
    },
    [onChangeFiles],
  );

  const onChangeIsOnBehalfOfLocal = useCallback(
    (value) => {
      setHasInputChanged(true);
      onChangeIsOnBehalfOf(value);
    },
    [onChangeIsOnBehalfOf],
  );

  const onDismissLocal = () => showSpinner || onDismiss();
  const onShowConditions = () => setIsConditionsPanelOpen(true);
  const onConfirmConditionsPanel = () => {
    setIsConditionsApproved(true);
    setIsConditionsPanelOpen(false);
  };

  const onDownloadFile = (value) =>
    value && downloadFileFromUrl(value.urlToDownload, value.fileName);

  return (
    <Panel
      isOpen={isOpen}
      onDismiss={onDismissLocal}
      isLightDismiss={true}
      onOuterClick={(_) => null}
      type={PanelType.large}
      headerText={
        isEditMode
          ? translator('pages.hearingDetails.hearingEditReply')
          : translator('pages.hearingDetails.hearingCreateReply')
      }
      closeButtonAriaLabel={translator('ariaLabel.panelClose')}
    >
      {!disableOnBehalfOf && (
        <div className="comment-panel-on-behalf-of-container">
          <Checkbox
            label={translator('pages.hearingDetails.answerOnBehalfOf')}
            styles={answerAsCheckboxStyles}
            onChange={(_, value) => onChangeIsOnBehalfOfLocal(value)}
            checked={isOnBehalfOf}
            disabled={isCurrentUserOwnerOfHearing}
          />
          {isOnBehalfOf && (
            <TextField
              id="answer-as-someone-else"
              placeholder={translator(
                'pages.hearingDetails.answerOnBehalfOfPlaceholder',
              )}
              styles={answerAsTextStyles}
              onChange={(_, value) => onChangeOnBehalfOfLocal(value)}
              value={onBehalfOf}
            />
          )}
        </div>
      )}
      <TextField
        id="comment-text"
        placeholder={translator(
          'pages.hearingDetails.hearingCreateReplyPlaceholder',
        )}
        onChange={(_, value) => onChangeLocalAnswer(value)}
        value={commentText}
        multiline
        autoAdjustHeight
        styles={commentTextStyles}
      />
      <AppendixUpload
        onUploadFiles={OnChangeFilesLocal}
        fileItems={commentFiles}
        onDeleteFile={onDeleteFile}
        maxFileSize={maxFileSizeInBytes}
        onDownloadFile={onDownloadFile}
        allowMultiple={true}
        headerText={translator('pages.hearingDetails.appendix')}
      />
      <PanelButtons
        onPrimaryClick={onCreateOrUpdateComment}
        onSecondaryClick={onDismissLocal}
        showSpinner={showSpinner}
        disablePrimaryButton={isButtonDisabled}
        isConditionsApproved={isConditionsApproved}
        setIsConditionsApproved={setIsConditionsApproved}
        onShowConditions={onShowConditions}
        disableTermsAndConditions={isTermsAndConditionsDisabled}
      />
      <ConditionsPanel
        isOpen={isConditionsPanelOpen}
        onConfirm={onConfirmConditionsPanel}
        onClose={() => setIsConditionsPanelOpen(false)}
        content={getAttr(globalContent, 'content')}
      />
    </Panel>
  );
};

export { EditCommentPanel };

const answerAsCheckboxStyles = {
  root: {
    paddingLeft: '0px',
    label: {
      '.ms-Checkbox-checkbox': {
        width: '30px',
        height: '30px',
      },
    },
  },
};

const answerAsTextStyles = {
  root: {
    width: '100%',
  },
};

const commentTextStyles = {
  root: {
    padding: '20px 0',
  },
  field: {
    width: '100%',
    minHeight: '300px',
    maxHeight: '600px',
    overflowY: 'scroll',
  },
};
